import React, { FC, useContext, MouseEvent, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import triggerTracking from '@blueprint/tracking';

import { SelectPropertyType } from './SelectPropertyType';
import { SelectBeds } from './SelectBeds';
import { SelectPriceForSale } from './SelectPriceForSale';
import { SelectPriceToRent } from './SelectPriceToRent';
import { SelectMaxTravelTime } from './SelectMaxTravelTime';
import { SelectTransportMode } from './SelectTransportMode';
import {
    SearchContext,
    SearchPageType,
    SearchSource
} from '../../../search/SearchContext';
import { LocationSearchInput } from '../LocationSearchInput/LocationSearchInput';
import {
    filtersValuesSelector,
    isTravelTimeSearchSelector,
    locationSelector,
    identifierSelector,
    locationTextSelector
} from '../../../reducers/filters';
import { selectCurrentSortOrder } from '../../../reducers/listingsSortOrder';
import {
    UpdateButtonWithIcon,
    LocationFilterWrapper,
    MobileSectionHeadline
} from './SearchInitiationMobile.styles';
import { BuyRentTabs } from '../../../homepage/components';
import { Tab } from '../../../homepage/components/HeroContainer/HeroContainer.types';

import { SelectRadius } from './SelectRadius';
import {
    SearchInitationWrapper,
    StyledButtonMobile,
    StyledButtonWrapperMobile,
    HousePricesLocationWrapper
} from '../SearchInitiation/SearchInitiation.styles';
import { getTranslator } from '../../i18n/getTranslator';
import searchTranslations from 'public/locales/en/search.json';
import { searchHousePrices, searchProperties } from 'search/helpers';
import { SearchSmall } from '@blueprint/icons';
import {
    withOptimizely,
    WithOptimizelyProps,
    selectForcedFeatures,
    getOptimizelyFeatureFlag
} from 'shared/optimizely';
import { SelectBedsPriceForSaleShort } from './SelectBedsPriceForSaleShort';
import { SelectPriceToRentShort } from './SelectPriceToRentShort';
import { SelectBedsToRentShort } from './SelectBedsToRentShort';
import { identifyUser } from 'shared/helpers/segment/identifyUser';

const t = getTranslator(searchTranslations);

const SEARCH_BUTTON_TEXT = t('filters.search');
const PRICE_RANGE = t('filters.priceRange');
const BEDROOMS = t('filters.bedrooms');
const EVENT = 'GAEvent';
const CATEGORY = 'search_filters';

type Props = {
    showRadius?: boolean;
    updateResultsText: string;
    searchType: Tab;
    eventCategory: 'homepage' | 'search results';
    searchSource: SearchSource;
    excludePageSize?: boolean;
};

const SearchInitiationMobileBase: FC<Props & WithOptimizelyProps> = (props) => {
    const {
        showRadius = true,
        updateResultsText,
        searchType,
        eventCategory,
        searchSource,
        excludePageSize = false,
        optimizely
    } = props;
    const router = useRouter();
    const { state, dispatch } = useContext(SearchContext);
    const { userAlertId } = state;

    const { selectedValue: locationValue } = useSelector(locationSelector);
    const { selectedValue: identifier } = useSelector(identifierSelector);
    const { selectedValue: locationTextValue } = useSelector(
        locationTextSelector
    );
    const [
        hasSubmittedLocationSearch,
        setHasSubmittedLocationSearch
    ] = useState(false);
    const currentSortOrder = useSelector(selectCurrentSortOrder);
    const filters = useSelector(filtersValuesSelector);
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);

    const [tabSelected, setTabSelected] = useState<Tab>(searchType);

    const isForSale = tabSelected === 'for-sale';
    const isHousePricesSearch = tabSelected === 'house-prices';

    const value = locationTextValue || locationValue;

    const handleSearchProperties = () => {
        const hasEnteredAValueOrSelectedFromDropdown = !!value;
        const eventLabel = hasEnteredAValueOrSelectedFromDropdown
            ? `${tabSelected}-success`
            : `${tabSelected}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        const excludeRadius = !showRadius;
        if (!value) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        searchProperties(
            userAlertId,
            router,
            dispatch,
            tabSelected as SearchPageType,
            searchSource,
            filters,
            currentSortOrder,
            excludeRadius,
            excludePageSize
        );
    };

    const handlePressUpdate = (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        evt.stopPropagation();

        triggerTracking({
            event: EVENT,
            eventCategory: CATEGORY,
            eventAction: 'click',
            eventLabel: 'quick_search_bar_update'
        });

        handleSearchProperties();
    };

    const handleSearchHousePrices = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            // No failure label as the button is disabled if there is no content
            eventLabel: 'house-prices-success'
        });

        searchHousePrices(value, identifier);
    };

    const showError = hasSubmittedLocationSearch && !value;

    const featureFlagOverrides = useSelector(selectForcedFeatures);
    const shortMobileInitiationEnabled = getOptimizelyFeatureFlag(
        'home_shorten_mobile_search_form',
        featureFlagOverrides,
        optimizely
    );

    useEffect(() => {
        if (window.innerWidth < 960) {
            identifyUser(window);

            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'optimizely',
                eventAction: 'home_shorten_mobile_search_form',
                eventLabel: shortMobileInitiationEnabled
                    ? 'variant'
                    : 'original'
            });
        }
    }, []);

    return (
        <>
            <BuyRentTabs
                onPress={setTabSelected}
                selected={tabSelected}
                includeHousePrices
                testIdSuffix={'mobile'}
            />

            {isHousePricesSearch ? (
                <SearchInitationWrapper hasExtraPaddingTop>
                    <HousePricesLocationWrapper>
                        <LocationSearchInput
                            id="header-location"
                            onLocationInputKeyDown={handleSearchHousePrices}
                            hasBorderRight={false}
                            searchType="house-prices"
                            value={locationValue}
                        />
                    </HousePricesLocationWrapper>
                    <StyledButtonWrapperMobile>
                        <StyledButtonMobile
                            type="button"
                            onClick={handleSearchHousePrices}
                            data-testid="mobile-house-prices-search-button"
                            disabled={!value}
                        >
                            {SEARCH_BUTTON_TEXT}
                        </StyledButtonMobile>
                    </StyledButtonWrapperMobile>
                </SearchInitationWrapper>
            ) : (
                <>
                    <LocationFilterWrapper>
                        <LocationSearchInput
                            id="filters-location-mobile"
                            onLocationInputKeyDown={handleSearchProperties}
                            onlyDisplayRemoveIconWhenActive={false}
                            value={locationValue}
                            showError={showError}
                        />
                    </LocationFilterWrapper>

                    {isTravelTimeSearch ? (
                        <>
                            <SelectMaxTravelTime />
                            <SelectTransportMode />{' '}
                        </>
                    ) : showRadius ? (
                        <SelectRadius />
                    ) : null}

                    {shortMobileInitiationEnabled ? (
                        <>
                            {isForSale ? (
                                <SelectBedsPriceForSaleShort />
                            ) : (
                                <>
                                    <MobileSectionHeadline>
                                        {PRICE_RANGE}
                                    </MobileSectionHeadline>
                                    <SelectPriceToRentShort />

                                    <MobileSectionHeadline>
                                        {BEDROOMS}
                                    </MobileSectionHeadline>
                                    <SelectBedsToRentShort />
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <MobileSectionHeadline>
                                {PRICE_RANGE}
                            </MobileSectionHeadline>
                            {isForSale ? (
                                <SelectPriceForSale />
                            ) : (
                                <SelectPriceToRent />
                            )}
                            <MobileSectionHeadline>
                                {BEDROOMS}
                            </MobileSectionHeadline>
                            <SelectBeds />
                        </>
                    )}

                    <SelectPropertyType />
                    <UpdateButtonWithIcon
                        isFullWidth
                        type="button"
                        onClick={handlePressUpdate}
                        data-testid="update-results-button"
                        title="Update"
                    >
                        <SearchSmall />
                        {updateResultsText}
                    </UpdateButtonWithIcon>
                </>
            )}
        </>
    );
};

export const SearchInitiationMobile = withOptimizely(
    SearchInitiationMobileBase
);
